/// <amd-module name="Repos/Editor/Components/ImageViewer" />
import * as React from "react";
import { IImageDiffViewerProps, IImageViewerProps } from "Repos/Common/Contracts/Editor";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";

export class ImageViewer extends VssComponent<IImageViewerProps> {
    public render() {
        return (
            <div className="repos-editor-image-container">
                <img className="repos-editor-image" src={this.props.url} title={this.props.content.metadata.fileName} />
            </div>
        );
    }

    public componentDidMount() {
        this.props.onLoaded && this.props.onLoaded();
    }
}

Components.add("repos-image-viewer", ImageViewer);

interface IImageDiffViewerState {
    canvasHeight: number;
    canvasWidth: number;
}

export class ImageDiffViewer extends VssComponent<IImageDiffViewerProps, IImageDiffViewerState> {
    public state = { canvasHeight: 100, canvasWidth: 100 };

    public render() {
        return (
            <Observer diffOrientation={this.props.diffOrientation}>
                {(diffProps: { diffOrientation: DiffViewerOrientation }) => {
                    const inline = diffProps.diffOrientation === DiffViewerOrientation.Inline;

                    return this.props.isPixelDiff ? (
                        <div className="repos-editor-image-container">
                            <canvas
                                className="repos-editor-image"
                                height={this.state.canvasHeight}
                                width={this.state.canvasWidth}
                                ref={this.drawPixelDiff}
                            />
                        </div>
                    ) : (
                        <div className={inline ? "flex-column flex-grow" : "flex-row flex-grow"}>
                            <div className="flex-grow flex-noshrink repos-image-diff-item">
                                <div className="repos-editor-image-container">
                                    <img
                                        className="repos-editor-image"
                                        src={this.props.originalContentUrl}
                                        title={this.props.originalContent.metadata.fileName}
                                    />
                                </div>
                            </div>
                            <div className="flex-grow flex-noshrink repos-image-diff-item">
                                <div className="repos-editor-image-container">
                                    <img
                                        className="repos-editor-image"
                                        src={this.props.modifiedContentUrl}
                                        title={this.props.modifiedContent.metadata.fileName}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        this.props.onLoaded && this.props.onLoaded();
    }

    private drawPixelDiff = async (canvas: HTMLCanvasElement) => {
        if (canvas) {
            const originalImagePromise = loadImage(this.props.originalContentUrl);
            const modifiedImagePromise = loadImage(this.props.modifiedContentUrl);

            const originalImage = await originalImagePromise;
            const modifiedImage = await modifiedImagePromise;

            const width = Math.max(originalImage.width, modifiedImage.width);
            const height = Math.max(originalImage.height, modifiedImage.height);
            this.setState({ canvasHeight: height, canvasWidth: width });

            const context = canvas.getContext("2d")!;

            context.drawImage(originalImage, 0, 0, originalImage.width, originalImage.height);
            const originalData = context.getImageData(0, 0, width, height);
            context.clearRect(0, 0, width, height);

            context.drawImage(modifiedImage, 0, 0, modifiedImage.width, modifiedImage.height);
            const modifiedData = context.getImageData(0, 0, width, height);

            const result = context.getImageData(0, 0, width, height);
            // Data is a linear array, each pixel is represented by 4 sequential values (RGBa)
            for (let i = 0; i < result.data.length; i += 4) {
                result.data[i] = Math.abs(originalData.data[i] - modifiedData.data[i]);
                result.data[i + 1] = Math.abs(originalData.data[i + 1] - modifiedData.data[i + 1]);
                result.data[i + 2] = Math.abs(originalData.data[i + 2] - modifiedData.data[i + 2]);
                result.data[i + 3] = 255; // No transparency
            }

            context.putImageData(result, 0, 0, 0, 0, width, height);
        }
    };
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise(resolve => {
        const img = new Image();
        img.src = url;
        img.onload = function () {
            resolve(img);
        };
    });
}

Components.add("repos-image-diff-viewer", ImageDiffViewer);
